import { type ReactNode } from 'react'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Wheat } from 'lucide-react'
import { getV1MeRetrieveQueryOptions, useV1RspoInstitutionsRetrieve } from '@/api/client'
import type { RspoInstitutionDetail } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { RspoTree } from '@/components/rspo/rspo-tree'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { zsckrMarker } from '@/lib/rspo-zsckr'

export const Route = createFileRoute('/_panel/katalog-rspo/$rspoId')({
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
    if (!/^\d+$/.test(params.rspoId)) {
      throw redirect({ to: '/katalog-rspo' })
    }
  },
  component: RspoDetailPage,
})

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-2 py-1.5 text-sm">
      <span className="w-48 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1">{value ?? '—'}</span>
    </div>
  )
}

function boolLabel(v: boolean | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return v ? 'Tak' : 'Nie'
}

function dateLabel(v: string | null | undefined): string {
  if (!v) return '—'
  return v
}

function identificationFields(d: RspoInstitutionDetail) {
  return (
    <>
      <FieldRow label="RSPO" value={d.rspo} />
      <FieldRow label="REGON" value={d.regon} />
      <FieldRow label="NIP" value={d.nip} />
      <FieldRow label="Typ" value={d.type} />
      <FieldRow label="Nazwa" value={d.name} />
      <FieldRow label="Pozycja w strukturze" value={d.structure_position} />
      <FieldRow label="Status publiczny" value={d.public_status} />
      <FieldRow label="Kategoria uczniów" value={d.student_category} />
      <FieldRow label="Specyfika" value={d.specificity} />
      <FieldRow label="Dyrektor" value={d.director} />
      <FieldRow label="Data założenia" value={dateLabel(d.founded_at)} />
      <FieldRow label="Data rozpoczęcia działalności" value={dateLabel(d.business_start_at)} />
      <FieldRow label="Data likwidacji" value={dateLabel(d.liquidated_at)} />
      <FieldRow label="Liczba uczniów" value={d.student_count} />
    </>
  )
}

function addressFields(d: RspoInstitutionDetail) {
  return (
    <>
      <FieldRow label="Województwo" value={d.address_voivodeship} />
      <FieldRow label="Powiat" value={d.address_district} />
      <FieldRow label="Gmina" value={d.address_commune} />
      <FieldRow label="Miejscowość" value={d.address_locality} />
      <FieldRow label="Rodzaj miejscowości" value={d.address_locality_kind} />
      <FieldRow label="Ulica" value={d.address_street} />
      <FieldRow label="Numer budynku" value={d.address_building_no} />
      <FieldRow label="Numer lokalu" value={d.address_premise_no} />
      <FieldRow label="Kod pocztowy" value={d.address_postal_code} />
      <FieldRow label="Poczta" value={d.address_post_office} />
      <FieldRow label="TERYT województwa" value={d.address_voivodeship_teryt} />
      <FieldRow label="TERYT powiatu" value={d.address_district_teryt} />
      <FieldRow label="TERYT gminy" value={d.address_commune_teryt} />
      <FieldRow label="TERYT miejscowości" value={d.address_locality_teryt} />
      <FieldRow label="TERYT ulicy" value={d.address_street_teryt} />
      <FieldRow label="Telefon" value={d.contact_phone} />
      <FieldRow label="Fax" value={d.contact_fax} />
      <FieldRow label="E-mail" value={d.contact_email} />
      <FieldRow label="Strona WWW" value={d.contact_website} />
      <FieldRow label="Języki" value={d.languages} />
      <FieldRow label="Obiekty sportowe" value={d.sports_areas} />
      <FieldRow label="Logopeda" value={boolLabel(d.has_speech_therapist)} />
      <FieldRow label="Psycholog" value={boolLabel(d.has_psychologist)} />
      <FieldRow label="Pedagog" value={boolLabel(d.has_pedagogue)} />
    </>
  )
}

function authorityFields(d: RspoInstitutionDetail) {
  return (
    <>
      <FieldRow label="ZSCKR" value={boolLabel(d.is_zsckr)} />
      <FieldRow label="Typ władzy" value={d.lead_authority_type} />
      <FieldRow label="Nazwa władzy" value={d.lead_authority_name} />
      <FieldRow label="REGON władzy" value={d.lead_authority_regon} />
      <FieldRow label="NIP władzy" value={d.lead_authority_nip} />
      <FieldRow label="Województwo władzy" value={d.lead_authority_voivodeship} />
      <FieldRow label="Powiat władzy" value={d.lead_authority_district} />
      <FieldRow label="Gmina władzy" value={d.lead_authority_commune} />
      <FieldRow label="Wydziały podstawowe" value={d.primary_departments} />
      <FieldRow label="Wydziały dodatkowe" value={d.additional_departments} />
      <FieldRow label="Zawody" value={d.profession} />
      <FieldRow label="Zawody artystyczne" value={d.artistic_profession} />
      <FieldRow label="Zawody KPSS" value={d.kpss_profession} />
      <FieldRow label="Pole BCU" value={d.bcu_field} />
    </>
  )
}

/** Loud, unmissable marker: the operator must see the institution kind before reading a field. */
function ZsckrBanner({ d }: { d: RspoInstitutionDetail }) {
  const marker = zsckrMarker(d)
  if (!marker) return null
  const isComplex = marker.tone === 'green'
  return (
    <div
      className={
        isComplex
          ? 'flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900 dark:bg-emerald-950'
          : 'flex items-center gap-3 rounded-xl border bg-muted px-4 py-3'
      }
    >
      <Wheat
        className={
          isComplex
            ? 'size-5 shrink-0 text-emerald-700 dark:text-emerald-300'
            : 'size-5 shrink-0 text-muted-foreground'
        }
      />
      <div className="min-w-0">
        <p
          className={
            isComplex
              ? 'font-semibold text-emerald-800 dark:text-emerald-200'
              : 'font-semibold text-foreground'
          }
        >
          {marker.title}
        </p>
        <p
          className={
            isComplex
              ? 'text-sm text-emerald-700 dark:text-emerald-300'
              : 'text-sm text-muted-foreground'
          }
        >
          {isComplex
            ? 'Placówka prowadzona przez Ministerstwo Rolnictwa i Rozwoju Wsi — kwalifikuje się do programu.'
            : `Jednostka działa w ramach zespołu szkół CKR${d.parent_name ? `: ${d.parent_name}` : ''}.`}
        </p>
      </div>
    </div>
  )
}

function RspoDetailPage() {
  const navigate = useNavigate()
  const { rspoId } = Route.useParams()
  const rspo = Number(rspoId)

  const query = useV1RspoInstitutionsRetrieve(rspo)
  const d = query.data?.status === 200 ? query.data.data : undefined

  if (query.isLoading) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => navigate({ to: '/katalog-rspo' })}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> Katalog RSPO
            </button>
          }
        />
        <div className="p-6 text-muted-foreground">Ładowanie…</div>
      </>
    )
  }

  if (!d) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => navigate({ to: '/katalog-rspo' })}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> Katalog RSPO
            </button>
          }
        />
        <div className="p-6 text-muted-foreground">
          Nie znaleziono placówki o numerze RSPO {rspoId}.
        </div>
      </>
    )
  }

  const hasChildren = (d.children ?? []).length > 0

  return (
    <>
      <PageHeader
        title={
          <button
            type="button"
            onClick={() => navigate({ to: '/katalog-rspo' })}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Katalog RSPO
          </button>
        }
      />
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">{d.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            RSPO {d.rspo}
            {d.type ? ` · ${d.type}` : ''}
          </p>
        </div>

        <ZsckrBanner d={d} />

        {d.school ? (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-muted/40 px-4 py-3">
            <span className="text-sm text-muted-foreground">
              Ta placówka jest już powiązana ze szkołą w programie:
            </span>
            <Link
              to="/szkoly/$szkolaId"
              params={{ szkolaId: String(d.school.id) }}
              className="text-sm font-medium underline underline-offset-2"
            >
              {d.school.name}
            </Link>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Identyfikacja</CardTitle>
          </CardHeader>
          <CardContent>{identificationFields(d)}</CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Adres + kontakt</CardTitle>
          </CardHeader>
          <CardContent>{addressFields(d)}</CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Władza prowadząca</CardTitle>
          </CardHeader>
          <CardContent>{authorityFields(d)}</CardContent>
        </Card>

        {hasChildren && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Jednostki podrzędne</h2>
            <RspoTree root={d} highlightRspo={rspo} />
          </div>
        )}
      </div>
    </>
  )
}
